import { auth } from './firebase';
import type { Community } from '@stash/shared-types';

interface ServiceError extends Error {
  status: number;
  data: unknown;
}

class CommunityService {
  private baseURL: string;
  private endpointURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || '/api';
    this.endpointURL = `${this.baseURL}/communities`;
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    if (!auth.currentUser) {
      return { 'Content-Type': 'application/json' };
    }

    const idToken = await auth.currentUser.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    };
  }

  async handleResponse(response: { ok: boolean; status: number; json: () => Promise<unknown> }): Promise<unknown> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { message?: string };
      const error = new Error(errorData.message || `HTTP ${response.status}`) as ServiceError;
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    const responseJson = await response.json() as { data?: unknown };
    return responseJson.data;
  }

  async getCommunityById(communityId: string): Promise<unknown> {
    if (!communityId) {
      throw new Error('Community ID is required');
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.endpointURL}/${communityId}`, { method: 'GET', headers });
    return this.handleResponse(response);
  }

  async listCommunities(privacyFilter: string | null = null, limit = 20, offset = 0): Promise<unknown> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams();

    if (privacyFilter) params.append('privacy', privacyFilter);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());

    const url = `${this.endpointURL}${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, { method: 'GET', headers });
    return this.handleResponse(response);
  }

  async updateCommunity(communityId: string, updates: Partial<Community>): Promise<unknown> {
    if (!communityId) {
      throw new Error('Community ID is required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.endpointURL}/${communityId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  async uploadImage(
    communityId: string,
    imageType: string,
    imageData: string,
    fileName: string | null = null,
  ): Promise<unknown> {
    if (!communityId || !imageType || !imageData) {
      throw new Error('Community ID, image type, and image data are required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const validImageTypes = ['logo', 'vibe-image', 'photos'];
    if (!validImageTypes.includes(imageType)) {
      throw new Error(`Invalid image type. Must be one of: ${validImageTypes.join(', ')}`);
    }

    const headers = await this.getAuthHeaders();
    const endpoint =
      imageType === 'photos'
        ? `${this.endpointURL}/${communityId}/photos`
        : `${this.endpointURL}/${communityId}/${imageType}`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageData, fileName }),
    });
    return this.handleResponse(response);
  }

  async deleteImage(communityId: string, imageType: string, imageId: string | null = null): Promise<unknown> {
    if (!communityId || !imageType) {
      throw new Error('Community ID and image type are required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const headers = await this.getAuthHeaders();
    let endpoint: string;

    if (imageType === 'photos' && imageId) {
      endpoint = `${this.endpointURL}/${communityId}/photos/${imageId}`;
    } else if (imageType === 'logo') {
      endpoint = `${this.endpointURL}/${communityId}/logo`;
    } else if (imageType === 'vibe-image') {
      endpoint = `${this.endpointURL}/${communityId}/vibe-image`;
    } else {
      throw new Error('Invalid image type or missing image ID for photos');
    }

    const response = await fetch(endpoint, { method: 'DELETE', headers });
    return this.handleResponse(response);
  }

  async addMember(communityId: string, stasherId: string, role = 'member'): Promise<unknown> {
    if (!communityId || !stasherId) {
      throw new Error('Community ID and stasher ID are required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const validRoles = ['member', 'editor', 'admin'];
    if (!validRoles.includes(role)) {
      throw new Error(`Invalid role. Must be one of: ${validRoles.join(', ')}`);
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.endpointURL}/${communityId}/members`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ stasherId, role }),
    });
    return this.handleResponse(response);
  }

  async removeMember(communityId: string, stasherId: string): Promise<unknown> {
    if (!communityId || !stasherId) {
      throw new Error('Community ID and stasher ID are required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.endpointURL}/${communityId}/members/${stasherId}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse(response);
  }

  async addVendor(communityId: string, vendorId: string): Promise<unknown> {
    if (!communityId || !vendorId) {
      throw new Error('Community ID and vendor ID are required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.endpointURL}/${communityId}/vendors`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ vendorId }),
    });
    return this.handleResponse(response);
  }

  async removeVendor(communityId: string, vendorId: string): Promise<unknown> {
    if (!communityId || !vendorId) {
      throw new Error('Community ID and vendor ID are required');
    }

    if (!auth.currentUser) {
      throw new Error('Authentication required');
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.endpointURL}/${communityId}/vendors/${vendorId}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse(response);
  }

  canUserEdit(community: { adminStasherIds?: string[]; editorStasherIds?: string[] } | null): boolean {
    if (!auth.currentUser || !community) {
      return false;
    }

    const userId = auth.currentUser.uid;
    return (
      (community.adminStasherIds?.includes(userId) ?? false) ||
      (community.editorStasherIds?.includes(userId) ?? false)
    );
  }

  isUserAdmin(community: { adminStasherIds?: string[] } | null): boolean {
    if (!auth.currentUser || !community) {
      return false;
    }

    const userId = auth.currentUser.uid;
    return community.adminStasherIds?.includes(userId) ?? false;
  }
}

export default CommunityService;
